import React, {Fragment} from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import {
    Badge,
    Button, Col,
    ConfigProvider,
    message,
    Modal, Pagination,
    Popconfirm,
    Row,
    Space,
    Switch,
    Table,
    Tooltip
} from "antd";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import {DEFAULT_PER_PAGE} from "../../config";
import NewFirewallRulesetModal from "./NewFirewallRulesetModal";
import {displayErrors, normalizeEnum} from "../../libs/utils";
import {DateTimeFormat} from "../shared/DateTimeFormat";
import {DeleteOutlined, ExclamationCircleOutlined, EyeOutlined, FolderOpenOutlined} from "@ant-design/icons";
import {bulkDeleteFirewallRulesets, deleteFirewallRuleset, loadFirewallRulesets, updateFirewallRulesetStatus} from "../../actions/firewall";
import EditFirewallRulesetModal from "./EditFirewallRulesetModal";
import {Link} from "react-router-dom";

class FirewallRulesets extends React.Component {
    state = {
        rulesets: [],
        loading: false,
        pagination: { current: 1, pageSize: DEFAULT_PER_PAGE, showSizeChanger: true, total: 0 },
        showNewRulesetModal: false,
        activeRuleset: '',
        showEditRulesetModal: false,
        selectedRulesets: []
    };

    componentDidMount() {
        this.props.updatePage('Firewall rulesets');
        this.loadRulesets();
    }

    loadRulesets(page = null, per_page = null) {
        if(page === null) {
            page = this.state.pagination.current;
        }

        if(per_page === null) {
            per_page = this.state.pagination.pageSize;
        }

        this.setState({ loading: true });

        this.props.loadFirewallRulesets(page, per_page, (res) => {
            this.setState({ rulesets: res.data.data, loading: false, pagination: { current: res.data.page, pageSize: res.data.per_page, total: res.data.total, showSizeChanger: true } });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
                this.setState({ loading: false })
            }
        });
    }

    changePage(data) {
        this.loadRulesets(data.current, data.pageSize);
    }

    toggleFirewallRulesetStatus(guid, status) {
        let data = {
            status: status ? 'ACTIVE' : 'DISABLED'
        };

        let rulesets = this.state.rulesets.map((item) => {
            if(item.guid === guid) {
                return {...item, status: data['status']};
            }

            return item;
        });

        this.setState({ rulesets: rulesets, loading: true });

        this.props.updateFirewallRulesetStatus(guid, data, () => {
            message.success('Firewall Ruleset successfully updated!');
            this.setState({ loading: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    deleteFirewallRuleset(guid) {
        this.setState({ loading: true });

        this.props.deleteFirewallRuleset(guid, () => {
            message.success('Firewall Ruleset successfully deleted!');
            this.loadRulesets();
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    confirmBulkDelete() {
        const { confirm } = Modal;

        confirm({
            title: 'Are you sure you want to delete these rulesets?',
            centered: true,
            okText: 'Yes',
            okType: 'danger',
            icon: <ExclamationCircleOutlined />,
            content: 'This can have significant impact on your domains and content!',
            onOk: () => {
                this.setState({ loading: true });

                let data = {
                    rules: this.state.selectedRules
                };

                this.props.bulkDeleteFirewallRulesets(data, () => {
                    message.success('Firewall rulesets successfully deleted!');
                    this.loadRulesets();
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        this.setState({ loading: false });
                        displayErrors(err.response.data);
                    }
                });
            }
        })
    }

    render() {
        const columns = [
            { title: '', render: (item, record) => <Switch size='small' checked={record.status === 'ACTIVE'} onChange={(e) => this.toggleFirewallRulesetStatus(record.guid, e)} /> },
            { title: 'Name', dataIndex: 'name' },
            { title: 'Created', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item} /> },
            { title: 'Status', dataIndex: 'status', render: (item) => <Badge status={item === 'ACTIVE' ? 'success' : 'error'} text={normalizeEnum(item)} /> },
            { title: 'Owner', render: (item, record) => record.is_system_ruleset ? 'System' : <Link to={'/accounts/companies/' + record.owner.guid}>{record.owner.name}</Link>},
            { title: '', align: 'right', render: (item, record) => {
                    return <Space>
                        <Button size='small' icon={<EyeOutlined />} onClick={() => this.setState({ activeRuleset: record.guid, showEditRulesetModal: true })}>View</Button>
                        <Popconfirm
                            title="Are you sure you want to delete this ruleset?"
                            onConfirm={() => this.deleteFirewallRuleset(record.guid)}
                            okText='Delete'
                            placement='topRight'
                            okButtonProps={{danger: true}}
                            cancelText='Cancel'>
                            <Button size='small' danger icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                    </Space>;
                }}
        ];

        return(
            <Fragment>
                <Row justify='space-between' style={{marginBottom: '10px'}}>
                    <Col>
                        <Space>
                            <Button size='small' type='text' icon={<PlusOutlined />} onClick={() => this.setState({ showNewRulesetModal: true })}>Add Firewall Ruleset</Button>
                            <Button size='small' type='text' disabled={this.state.selectedRulesets.length === 0} icon={<DeleteOutlined />} onClick={() => this.confirmBulkDelete()}>Delete</Button>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Tooltip title='Reload table' placement='left'><Button onClick={() => this.loadRulesets()} icon={<UndoOutlined />} size='small' type='circle'></Button></Tooltip>
                            <Pagination onChange={(page, pageSize) => this.loadRulesets(page, pageSize)} size='small' {...this.state.pagination} />
                        </Space>
                    </Col>
                </Row>

                <ConfigProvider renderEmpty={() => <div style={{ textAlign: 'center' }}>
                    <FolderOpenOutlined style={{ fontSize: 40 }} />
                    <p>Nothing to see here...</p>
                    <Button icon={<PlusOutlined />} onClick={() => this.setState({ showNewRulesetModal: true })}>Add Firewall Ruleset</Button>
                </div>}>
                    <Table
                        dataSource={this.state.rulesets}
                        rowSelection={{
                            type: 'checkbox',
                            onChange: (rulesets) => this.setState({ selectedRulesets: rulesets })
                        }}
                        columns={columns}
                        rowKey={(item) => item.guid}
                        pagination={false}
                        footer={() => <Row justify='space-between'>
                            <Col>{this.state.selectedRulesets.length + ' selected'}</Col>
                            <Col><Pagination onChange={(page, pageSize) => this.loadRulesets(page, pageSize)} size='small' {...this.state.pagination} /></Col>
                        </Row>}
                        loading={this.state.loading} />
                </ConfigProvider>

                <NewFirewallRulesetModal
                    showModal={this.state.showNewRulesetModal}
                    reloadRulesets={() => this.loadRulesets()}
                    close={() => this.setState({ showNewRulesetModal: false })} />

                <EditFirewallRulesetModal
                    showModal={this.state.showEditRulesetModal}
                    reloadRulesets={() => this.loadRulesets()}
                    guid={this.state.activeRuleset}
                    close={() => this.setState({ showEditRulesetModal: false })} />
            </Fragment>
        );
    }
}

export default connect(null, { updatePage, loadFirewallRulesets, updateFirewallRulesetStatus, deleteFirewallRuleset, bulkDeleteFirewallRulesets })(FirewallRulesets);